/* eslint-disable react-hooks/exhaustive-deps */
import React, { MouseEventHandler, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CustomToolTip from '../../core/CustomToolTip/CustomToolTip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/free-regular-svg-icons';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { ReactComponent as InfoIcon } from '../../assets/images/info.svg';

import './PriceCalculator.css';
import { formatNumber, numberRegex } from '../../utils/helpers';
import { useVideo } from '../../context/VideoContext';
import IconPro from '../Icons/IconPro';
import { PaisesDestino } from './PaisesDestino';
import { Tooltip } from '@mui/material';
import { useRemainingCalculations } from '../../hooks/useRemainingCalculations';
import { useAuth } from '../../context/AuthContext';
import sectoresMinorista from './UtilidadMinorista.json';
import truckTypeOptions from './truckTypeOptions.json';
import { getApiDailyParamName, postApiSeaRatesGetD2D, postApiSeaRatesGetD2DAir } from '../../api/interconectar-api';
import CircularLoading from '../../shared/CircularLoading/CircularLoading';
import { usePostApiSubscriptionRegisterSubscriptionUse } from '../../api/interconectar-api';
import { IconDropDownArrow } from '../Icons';
import { useProUser } from '../../context/ProUserContext';

interface Sector {
	sector: {
		es: string;
		en: string;
	};
	margin: number;
}

// Agregar una interfaz para el formato ciudad-país
interface CityWithCountry {
	city: string;
	country: string;
}

const PriceCalculatorStepFour = ({
	handleClickOpen,
	handleContinueClick,
}: {
	handleClickOpen: MouseEventHandler<HTMLDivElement>;
	handleContinueClick: () => void;
}) => {
	const { t, i18n } = useTranslation();
	const { setVideoName } = useVideo();
	const { state } = useAuth();
	const { refetchProStatus } = useProUser();
	const {
		data: remainingCalcs,
		loading: loadingCalcs,
		error: errorCalcs,
		refetch: refetchRemainingCalcs,
	} = useRemainingCalculations(process.env.REACT_APP_PRICE_CALCULATOR_ID || '353');
	const { mutate: registerSubscriptionUse } = usePostApiSubscriptionRegisterSubscriptionUse();
	const [formFourValues, setFormFourValues] = useState<any>();
	const [tooltipOpen, setTooltipOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState(() => {
		try {
			const formFourStr = localStorage.getItem('formFour');
			if (formFourStr) {
				const formFour = JSON.parse(formFourStr);
				return formFour?.retailerCity || '';
			}
		} catch (error) {
			console.error('Error parsing formFour from localStorage:', error);
		}
		return '';
	});
	const [showResults, setShowResults] = useState(false);
	const [isMouseOverResults, setIsMouseOverResults] = useState(false);
	const [loadingUtilidadMayorista, setLoadingUtilidadMayorista] = useState<boolean>(true);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [loadingSeaRates, setLoadingSeaRates] = useState<boolean>(false);
	const [errorSeaRates, setErrorSeaRates] = useState<string | null>(null);
	const [seaRatesData, setSeaRatesData] = useState<any>(null);

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm();

	const onSubmit = (data: any) => {
		localStorage.setItem('formFour', JSON.stringify(data));
		setFormFourValues(data);

		if (state?.isAuthenticated && remainingCalcs?.userSubscriptionId) {
			const userId = localStorage.getItem('userId');
			if (userId) {
				setIsSubmitting(true);
				registerSubscriptionUse(
					{
						data: {
							userId,
							userSubscriptionId: remainingCalcs.userSubscriptionId,
						},
					},
					{
						onSuccess: () => {
							refetchRemainingCalcs();
							// Refrescar el estado pro del usuario después de la comparación
							// ya que el servicio resta un uso del pack
							// Agregar un delay para asegurar que la API haya procesado la actualización
							setTimeout(() => {
								refetchProStatus();
								setIsSubmitting(false);
								handleContinueClick();
							}, 800);
						},
						onError: () => {
							setIsSubmitting(false);
						},
					},
				);
			}
		} else {
			handleContinueClick();
		}

		data.marketingSegment === 'Retail' || data.marketingSegment === 'Minorista'
			? setVideoName('fourthRetailVideo')
			: data.marketingSegment === 'Ecommerce'
				? setVideoName('fourthDeliveryVideo')
				: setVideoName('');
	};

	useEffect(() => {
		try {
			const formFourStr = localStorage.getItem('formFour');
			if (formFourStr) {
				const formFour = JSON.parse(formFourStr);
				if (formFour && typeof formFour === 'object' && Object.keys(formFour).length > 0) {
					// Validar y corregir retailTruckType si es necesario
					if (formFour.retailTruckType) {
						const truckTypeKeys = Object.keys(truckTypeOptions[0]);
						const truckTypeExists = truckTypeKeys.some(
							(key) => key.toLowerCase() === formFour.retailTruckType.toLowerCase(),
						);

						if (!truckTypeExists) {
							console.log('Invalid retailTruckType value:', formFour.retailTruckType);
							formFour.retailTruckType = truckTypeKeys[0];
						} else {
							const correctKey = truckTypeKeys.find(
								(key) => key.toLowerCase() === formFour.retailTruckType.toLowerCase(),
							);
							if (correctKey && correctKey !== formFour.retailTruckType) {
								console.log('Correcting retailTruckType case:', formFour.retailTruckType, '->', correctKey);
								formFour.retailTruckType = correctKey;
							}
						}
					}

					setFormFourValues(formFour);
					if (formFour.retailerCity) {
						setSearchTerm(formFour.retailerCity);
					}
				}
			}
		} catch (error) {
			console.error('Error parsing formFour from localStorage:', error);
		}
	}, []);

	useEffect(() => {
		if (formFourValues) {
			reset((prev) => ({ ...prev, ...formFourValues }));
		}
	}, [formFourValues, reset]);

	useEffect(() => {
		setVideoName('');
	}, []);

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}, []);

	// Modificar la función getAllCities para incluir el país
	const getAllCities = (): CityWithCountry[] => {
		return Object.entries(PaisesDestino).flatMap(([country, cities]) =>
			cities.map((city) => ({
				city,
				country,
			})),
		);
	};

	// Modificar la función getFilteredCities para trabajar con el nuevo formato
	const getFilteredCities = () => {
		const allCities = getAllCities();
		if (!searchTerm) return allCities;

		return allCities.filter(
			(item) =>
				item.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
				item.country.toLowerCase().includes(searchTerm.toLowerCase()),
		);
	};

	// Modificar la función handleCitySelect para manejar el nuevo formato
	const handleCitySelect = (cityData: CityWithCountry) => {
		setSearchTerm(cityData.city);
		setShowResults(false);
		setFormFourValues({
			...formFourValues,
			retailerCity: cityData.city,
		});

		// Marcar que retailerCity cambió para trigger la API call
		localStorage.setItem('retailerCityChanged', 'true');
	};

	// Combine all loading states
	useEffect(() => {
		const anyLoading = loadingUtilidadMayorista || loadingCalcs;
		setIsLoading(anyLoading);

		// Si no hay ningún loading y es la carga inicial, marcamos que ya no es carga inicial
		if (!anyLoading && isInitialLoad) {
			setIsInitialLoad(false);
		}
	}, [loadingUtilidadMayorista, loadingCalcs, isInitialLoad]);

	useEffect(() => {
		if (state?.isAuthenticated && remainingCalcs && remainingCalcs.remaining > 0 && !formFourValues?.wholesalerMargin) {
			setLoadingUtilidadMayorista(true);
			getApiDailyParamName('UtilidadMayorista')
				.then((response) => {
					if (response.result?.value) {
						setFormFourValues((prev: Record<string, any>) => ({
							...prev,
							wholesalerMargin: response.result?.value,
						}));
					}
				})
				.catch((error) => {
					console.error('Error fetching UtilidadMayorista:', error);
				})
				.finally(() => {
					setLoadingUtilidadMayorista(false);
				});
		} else {
			// Si no necesitamos cargar UtilidadMayorista, marcamos loading como false
			setLoadingUtilidadMayorista(false);
		}
	}, [state?.isAuthenticated, remainingCalcs, formFourValues?.wholesalerMargin]);

	// postApiSeaRatesGetD2D
	useEffect(() => {
		// console.log('D2D useEffect triggered - formFourValues:', formFourValues);
		// console.log('D2D useEffect triggered - isInitialLoad:', isInitialLoad);

		// Solo ejecutar si no estamos en carga inicial y tenemos datos cargados
		if (state?.isAuthenticated && remainingCalcs && remainingCalcs.remaining > 0 && !isInitialLoad) {
			if (formFourValues?.retailerCity && formFourValues?.retailTruckType) {
				// Verificar si formTwo cambió (containerId), si importerCity cambió, si retailerCity cambió, o si retailTruckType cambió
				const formTwoChanged = localStorage.getItem('formTwoChanged') === 'true';
				const importerCityChanged = localStorage.getItem('importerCityChanged') === 'true';
				const retailerCityChanged = localStorage.getItem('retailerCityChanged') === 'true';
				const retailTruckTypeChanged = localStorage.getItem('retailTruckTypeChanged') === 'true';

				// Hacer la llamada a la API si formTwo cambió, si importerCity cambió, si retailerCity cambió, o si retailTruckType cambió
				if (formTwoChanged || importerCityChanged || retailerCityChanged || retailTruckTypeChanged) {
					const fetchD2D = async () => {
						setLoadingSeaRates(true);
						setErrorSeaRates(null);
						try {
							const formTwoStr = localStorage.getItem('formTwo');
							const formTwo = formTwoStr ? JSON.parse(formTwoStr) : {};
							const formThreeStr = localStorage.getItem('formThree');
							const formThree = formThreeStr ? JSON.parse(formThreeStr) : {};

							// Verificar si es transporte aéreo
							const isAirFreight = formTwo?.meansTransportation === 'Air';

							let response;

							if (isAirFreight) {
								// Para transporte aéreo
								const airRequestData = {
									airportOriginId: formTwo?.airportId || null,
									destinationCity: formFourValues?.retailerCity || null,
									volumeInCubicMeters: parseFloat(formTwo?.airTarifCode) || 0,
									weightKg: parseFloat(formTwo?.airTotalWeightPlusPackaging) || 0,
								};
								console.log('D2D Air Request data (Step 4):', airRequestData);
								response = await postApiSeaRatesGetD2DAir(airRequestData);
							} else {
								// Para transporte marítimo/multimodal
								const seaRequestData = {
									containerTypeId: formTwo?.containerId,
									destinationCity: formFourValues?.retailerCity || null,
									originCity: formThree?.importerCity || null,
									// retailTruckType: formFourValues?.retailTruckType || null,
								};
								console.log('D2D Sea Request data (Step 4):', seaRequestData);
								response = await postApiSeaRatesGetD2D(seaRequestData);
							}

							if (response?.result?.routeTariffPrice !== undefined) {
								setSeaRatesData(response.result);

								// Actualizar el campo costTransportationWholesalerRetailerUsd con routeTariffPrice
								const updatedFormValues = {
									...formFourValues,
									costTransportationWholesalerRetailerUsd: response.result.routeTariffPrice.toString(),
								};

								setFormFourValues(updatedFormValues);

								// Limpiar las banderas de cambios
								localStorage.removeItem('formTwoChanged');
								localStorage.removeItem('importerCityChanged');
								localStorage.removeItem('retailerCityChanged');
								localStorage.removeItem('retailTruckTypeChanged');

								// Limpiar la bandera de cambios del formOne
								localStorage.removeItem('formOneChanged');
							}
						} catch (err: any) {
							console.error('Error fetching D2D (Step 4):', err);
							setErrorSeaRates(err.message || 'Error desconocido');
						} finally {
							setLoadingSeaRates(false);
						}
					};

					fetchD2D();
				} else {
					console.log('No se hace llamada a la API D2D (Step 4) - no hay cambios relevantes');
				}
			}
		}
	}, [
		state?.isAuthenticated,
		remainingCalcs,
		formFourValues?.retailerCity,
		formFourValues?.retailTruckType,
		isInitialLoad,
	]);

	// Mostrar loading solo durante la carga inicial
	if (isInitialLoad && isLoading) {
		return <CircularLoading />;
	}

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<div className="exporter-test__read-only">
				<div className="price-calculator__container-main">
					<div className="price-calculator___inner">
						<div className="container price-calculator___container">
							<div className="row">
								<div className="col-12">
									<span className="price-calculator__container-title">{t('price_calculator.step_4.subtitle')}</span>
								</div>
							</div>
							<div className="price-calculator-grid">
								<div className="price-calculator-half-row">
									<div className="col-12 col-sm-12 col-md-12 ">
										<div className="price-calculator__field-with-icon label-container container_input__pro">
											<input
												type="number"
												className="price-calculator__textbox-general-col2"
												{...register('wholesalerMargin', { required: true })}
												value={!!formFourValues?.wholesalerMargin ? formFourValues?.wholesalerMargin : ''}
												onChange={(e) => {
													if (e.target.value === '' || numberRegex.test(e.target.value)) {
														setFormFourValues({ ...formFourValues, wholesalerMargin: formatNumber(e.target.value) });
													}
												}}
											/>
											<label className="label-input">{t('price_calculator.step_4.wholesalerMargin.placeholder')}</label>
											<div style={{ marginLeft: '8px' }}>
												<CustomToolTip
													title=""
													tooltipText={t('price_calculator.step_4.wholesalerMargin.tooltip')}>
													<FontAwesomeIcon
														icon={faCircleQuestion}
														className="font-size: 18px"
													/>
												</CustomToolTip>
											</div>
										</div>
										<div className="label__pro">
											<IconPro
												width={13}
												height={10}
												fill="#BBC2C7"
											/>
											{t('price_calculator.label_pro.description')}
										</div>
										{!!errors?.wholesalerMargin && (
											<div className="error-container">
												<InfoIcon fill="#F8A0D2" />
												<div className="error-message">{t('price_calculator.step_4.error_message')}</div>
											</div>
										)}
									</div>
								</div>

								<div className="price-calculator-half-row">
									<div className="col-12 col-sm-12 col-md-12">
										<div className="price-calculator__field-with-icon label-container">
											<input
												type="number"
												className="price-calculator__textbox-general-col2"
												{...register('quantityMerchandiseRetailer', {
													required: true,
													validate: (value) => {
														const formOneData = JSON.parse(localStorage.getItem('formOne') || '{}');
														const quantityGoods = Number(formOneData.quantityGoods);
														return (
															Number(value) <= quantityGoods || 'Value must be less than or equal to quantity goods'
														);
													},
												})}
												value={
													!!formFourValues?.quantityMerchandiseRetailer
														? formFourValues?.quantityMerchandiseRetailer
														: ''
												}
												onChange={(e) => {
													if (e.target.value === '' || numberRegex.test(e.target.value)) {
														const formOneData = JSON.parse(localStorage.getItem('formOne') || '{}');
														const quantityGoods = Number(formOneData.quantityGoods);
														const newValue = Number(formatNumber(e.target.value));

														if (newValue <= quantityGoods) {
															setFormFourValues({
																...formFourValues,
																quantityMerchandiseRetailer: formatNumber(e.target.value),
															});
														}
													}
												}}
											/>
											<label className="label-input">
												{t('price_calculator.step_4.quantityMerchandiseRetailer.placeholder')}
											</label>
											<div style={{ marginLeft: '8px' }}>
												<CustomToolTip
													title=""
													tooltipText={t('price_calculator.step_4.quantityMerchandiseRetailer.tooltip')}>
													<FontAwesomeIcon
														icon={faCircleQuestion}
														className="font-size: 18px"
													/>
												</CustomToolTip>
											</div>
										</div>
										{!!errors?.quantityMerchandiseRetailer && (
											<div className="error-container">
												<InfoIcon fill="#F8A0D2" />
												<div className="error-message">{t('price_calculator.step_4.error_message')}</div>
											</div>
										)}
									</div>
								</div>

								<div className="price-calculator-half-row">
									<div className="col-12 col-sm-12 col-md-12">
										<div className="price-calculator__pais">
											<div className="price-calculator__search-container">
												<input
													{...register('retailerCity', { required: true })}
													type="text"
													className="price-calculator__search-input"
													value={searchTerm}
													onChange={(e) => {
														setSearchTerm(e.target.value);
														setShowResults(true);
													}}
													onFocus={() => setShowResults(true)}
													onBlur={() => {
														if (!isMouseOverResults) {
															setShowResults(false);
														}
													}}
												/>
												<label className="label-input">{t('price_calculator.step_4.retailerCity.placeholder')}</label>
												{showResults && (
													<div
														className="price-calculator__search-results"
														onMouseEnter={() => setIsMouseOverResults(true)}
														onMouseLeave={() => setIsMouseOverResults(false)}>
														<div className="price-calculator__search-result">
															{getFilteredCities().map((cityData, index) => (
																<span
																	key={index}
																	onClick={() => {
																		handleCitySelect(cityData);
																		setIsMouseOverResults(false);
																	}}
																	style={{
																		cursor: 'pointer',
																		fontWeight: cityData.city === formFourValues?.retailerCity ? 'bold' : 'normal',
																		color: cityData.city === formFourValues?.retailerCity ? '#fff' : '#bbc2c7',
																	}}>
																	{`${cityData.city} (${cityData.country})`}
																</span>
															))}
														</div>
													</div>
												)}
											</div>
											<div style={{ marginLeft: '8px' }}>
												<CustomToolTip
													title=""
													tooltipText={t('price_calculator.step_4.retailerCity.tooltip')}>
													<FontAwesomeIcon
														icon={faCircleQuestion}
														className="font-size: 18px"
													/>
												</CustomToolTip>
											</div>
										</div>
										{!!errors?.retailerCity && (
											<div className="error-container">
												<InfoIcon fill="#F8A0D2" />
												<div className="error-message">{t('price_calculator.step_4.error_message')}</div>
											</div>
										)}
									</div>
								</div>

								{state?.isAuthenticated && remainingCalcs && remainingCalcs.remaining > 0 && (
									<div className="price-calculator-half-row">
										<div className="col-12 col-sm-12 col-md-12">
											<div className="price-calculator__distance">
												<div className="price-calculator__field-with-icon label-container ">
													<div className="label-container">
														<label className="label-input">
															{t('price_calculator.step_4.camionComercializacion.placeholder')}
														</label>

														<select
															{...register('retailTruckType', { required: true })}
															value={formFourValues?.retailTruckType || ''}
															onChange={(e) => {
																const selectedCamion = e.target.value;
																setFormFourValues({
																	...formFourValues,
																	retailTruckType: selectedCamion,
																});

																// Marcar que retailTruckType cambió para trigger la API call
																localStorage.setItem('retailTruckTypeChanged', 'true');
															}}
															className={`label-container__input input-label-select ${errors?.retailTruckType ? 'error' : ''}`}>
															<option
																value=""
																disabled>
																{t('select.placeholder')}
															</option>

															{Object.entries(truckTypeOptions[0]).map(
																([key, value]: [string, { es: string; en: string; hi: string }]) => {
																	const currentLang = i18n.language;
																	let lang: 'es' | 'en' | 'hi' = 'es';
																	if (currentLang === 'en') lang = 'en';
																	if (currentLang === 'hi') lang = 'hi';
																	// Si el idioma comienza con 'es', lo tratamos como español
																	if (currentLang.startsWith('es')) lang = 'es';
																	return (
																		<option
																			key={key}
																			value={key}>
																			{value[lang] || key}
																		</option>
																	);
																},
															)}
														</select>
														<IconDropDownArrow className="input-label-select-arrow" />
													</div>

													<div style={{ marginLeft: '8px' }}>
														<Tooltip
															onClick={(e) => {
																e.preventDefault();
																e.stopPropagation();
																setTooltipOpen(!tooltipOpen);
															}}
															open={tooltipOpen}
															onClose={() => setTooltipOpen(false)}
															disableHoverListener
															disableFocusListener
															disableTouchListener
															classes={{ tooltip: 'custom-tooltip' }}
															title={
																<React.Fragment>
																	<div
																		dangerouslySetInnerHTML={{
																			__html: t('price_calculator.step_4.camionComercializacion.tooltip'),
																		}}
																	/>
																</React.Fragment>
															}>
															<FontAwesomeIcon
																icon={faCircleQuestion}
																className="font-size: 18px"
															/>
														</Tooltip>
													</div>
												</div>

												{!!errors?.retailTruckType && (
													<div className="error-container">
														<InfoIcon fill="#F8A0D2" />
														<div className="error-message">{t('price_calculator.step_4.error_message')}</div>
													</div>
												)}
											</div>
										</div>
									</div>
								)}

								<div className="price-calculator-half-row">
									<div className="col-12 col-sm-12 col-md-12">
										{loadingSeaRates && (
											<div className="price-calculator__field-loading">
												<CircularLoading />
											</div>
										)}

										{!loadingSeaRates && (
											<>
												<div className="price-calculator__field-with-icon label-container container_input__pro">
													<input
														type="number"
														className="price-calculator__textbox-general-col2"
														{...register('costTransportationWholesalerRetailerUsd', { required: true })}
														value={
															!!formFourValues?.costTransportationWholesalerRetailerUsd
																? formFourValues?.costTransportationWholesalerRetailerUsd
																: ''
														}
														onChange={(e) => {
															if (e.target.value === '' || numberRegex.test(e.target.value)) {
																setFormFourValues({
																	...formFourValues,
																	costTransportationWholesalerRetailerUsd: formatNumber(e.target.value),
																});
															}
														}}
													/>
													<label className="label-input">
														{t('price_calculator.step_4.costTransportationWholesalerRetailerUsd.placeholder')}
													</label>
													<div style={{ marginLeft: '8px' }}>
														<CustomToolTip
															title=""
															tooltipText={t(
																'price_calculator.step_4.costTransportationWholesalerRetailerUsd.tooltip',
															)}>
															<FontAwesomeIcon
																icon={faCircleQuestion}
																className="font-size: 18px"
															/>
														</CustomToolTip>
													</div>
												</div>
												<div className="label__pro">
													<IconPro
														width={13}
														height={10}
														fill="#BBC2C7"
													/>
													{t('price_calculator.label_pro.description')}
												</div>
												{(!!errors?.distance || !!errors?.costTransportationWholesalerRetailerUsd) && (
													<div className="error-container">
														<InfoIcon fill="#F8A0D2" />
														<div className="error-message">{t('price_calculator.step_4.error_message')}</div>
													</div>
												)}
											</>
										)}
									</div>
								</div>

								<div className="price-calculator-half-row">
									<div className="col-12 col-sm-12 col-md-12">
										<div className="label-container">
											<label className="label-input">{t('price_calculator.step_4.marketingSegment.placeholder')}</label>
											<select
												className="label-container__input input-label-select"
												{...register('marketingSegment', { required: true })}
												value={!!formFourValues?.marketingSegment ? formFourValues?.marketingSegment : ''}
												onChange={(e) => {
													const currentLang = i18n.language;
													const lang = currentLang.startsWith('es') ? 'es' : ('en' as const);
													const selectedSector = (sectoresMinorista as Sector[]).find(
														(sector) => sector.sector[lang] === e.target.value,
													);
													setFormFourValues({
														...formFourValues,
														marketingSegment: e.target.value,
														retailerMargin: selectedSector ? selectedSector.margin.toString() : '',
													});
												}}>
												<option
													value=""
													disabled>
													{t('select.placeholder')}
												</option>
												{state?.isAuthenticated && remainingCalcs && remainingCalcs.remaining > 0 ? (
													(sectoresMinorista as Sector[])
														.filter((sector) => {
															const currentLang = i18n.language;
															const lang = currentLang.startsWith('es') ? 'es' : ('en' as const);
															return sector && sector.sector && sector.sector[lang];
														})
														.sort((a, b) => {
															const currentLang = i18n.language;
															const lang = currentLang.startsWith('es') ? 'es' : ('en' as const);
															const aValue = a.sector[lang] || '';
															const bValue = b.sector[lang] || '';
															return aValue.localeCompare(bValue);
														})
														.map((sector) => {
															const currentLang = i18n.language;
															const lang = currentLang.startsWith('es') ? 'es' : ('en' as const);
															return (
																<option
																	key={sector.sector[lang]}
																	value={sector.sector[lang]}
																	className="exporter-test__select-font">
																	{sector.sector[lang]}
																</option>
															);
														})
												) : (
													<>
														<option
															value="Retail"
															className="exporter-test__select-font">
															Retail
														</option>
														<option
															value="Ecommerce"
															className="exporter-test__select-font">
															Ecommerce
														</option>
														<option
															value="Minorista"
															className="exporter-test__select-font">
															Minorista
														</option>
													</>
												)}
											</select>
											<IconDropDownArrow className="input-label-select-arrow" />
										</div>
										{!!errors?.marketingSegment && (
											<div className="error-container">
												<InfoIcon fill="#F8A0D2" />
												<div className="error-message">{t('price_calculator.step_4.error_message')}</div>
											</div>
										)}
									</div>
								</div>
								<div className="price-calculator-half-row">
									<div className="col-12 col-sm-12 col-md-12">
										<div className="price-calculator__field-with-icon label-container container_input__pro">
											<input
												type="number"
												className="price-calculator__textbox-general-col2"
												{...register('retailerMargin', { required: true })}
												value={!!formFourValues?.retailerMargin ? formFourValues?.retailerMargin : ''}
												onChange={(e) => {
													if (e.target.value === '' || numberRegex.test(e.target.value)) {
														setFormFourValues({ ...formFourValues, retailerMargin: formatNumber(e.target.value) });
													}
												}}
											/>
											<label className="label-input">{t('price_calculator.step_4.retailerMargin.placeholder')}</label>
											<div style={{ marginLeft: '8px' }}>
												<CustomToolTip
													title=""
													tooltipText={t('price_calculator.step_4.retailerMargin.tooltip')}>
													<FontAwesomeIcon
														icon={faCircleQuestion}
														className="font-size: 18px"
													/>
												</CustomToolTip>
											</div>
										</div>
										<div className="label__pro">
											<IconPro
												width={13}
												height={10}
												fill="#BBC2C7"
											/>
											{t('price_calculator.label_pro.description')}
										</div>
										{!!errors?.retailerMargin && (
											<div className="error-container">
												<InfoIcon fill="#F8A0D2" />
												<div className="error-message">{t('price_calculator.step_4.error_message')}</div>
											</div>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
					<div className="price-calculator__container-footer">
						<div
							className="price-calculator__footer-link"
							onClick={handleClickOpen}>
							{t('price_calculator.footer.link')}
						</div>
						<button
							type="submit"
							className={`price-calc__next ${isSubmitting ? 'disabled' : ''}`}
							disabled={isSubmitting}>
							{!isSubmitting && <div className="btn-test-next-text">{t('price_calculator.footer.button')}</div>}
							{!isSubmitting && (
								<FontAwesomeIcon
									icon={faArrowRight}
									color="black"
								/>
							)}
							{isSubmitting && <CircularLoading />}
						</button>
					</div>
				</div>
			</div>
		</form>
	);
};

export default PriceCalculatorStepFour;
